import { useGetTasksQuery } from 'api/collaborateApi';
import { useCallback, useEffect } from 'react';
import { useSelector, useStore } from 'react-redux';
import { CollaborateTaskData, GetTasksParams } from 'types/api/collaborate';
import {
  getHostAndPath,
  getPageUrl,
  getUrlParams,
  isInIframe,
} from 'utils/proxyUtils';
import { RootState, useAppDispatch } from '../store';
import {
  getNotifiedUsersFilter,
  getOrderByFilter,
  getPageFilter,
  getTaskStatusFilter,
  getTaskTypesFilter,
  getTextSearchFilter,
} from '../store/filtersSlice';
import { getSelectedSiteInfo, getSiteTotalTasks } from '../store/siteInfoSlice';
import { getTaskOffset, getTasks, setTasks } from '../store/tasksSlice';
import { getUser } from '../store/userSlice';

const roundUpToNearest10 = (value?: number): number => {
  if (value === undefined || value < 0) {
    return 10;
  }
  return Math.ceil(value / 10) * 10;
};

const useCurrentTasks = () => {
  const store = useStore<RootState>();
  const getCurrentTasks = useCallback(() => {
    return getTasks(store.getState());
  }, [store]);
  return getCurrentTasks;
};

export const useTasksNew = () => {
  const dispatch = useAppDispatch();
  // Get site info if siteId isn't provided
  const getCurrentTasks = useCurrentTasks();
  const siteInfo = useSelector(getSelectedSiteInfo);
  const siteId = siteInfo?.id;
  const userInfo = useSelector(getUser);
  const totalTasksRaw = useSelector(getSiteTotalTasks);
  const offset = useSelector(getTaskOffset);
  const totalTasks = roundUpToNearest10(totalTasksRaw);
  const workspaceId = siteInfo?.workspace_id;
  const currentPageUrl = getPageUrl() ?? undefined;

  // Get all filter values from Redux store (only if not in simple mode)
  const pageFilter = useSelector(getPageFilter);
  const notifiedUsers = useSelector(getNotifiedUsersFilter);
  const orderBy = useSelector(getOrderByFilter);
  const taskStatusFilter = useSelector(getTaskStatusFilter);
  const taskTypesFilter = useSelector(getTaskTypesFilter);
  const textSearch = useSelector(getTextSearchFilter);

  // Get URL parameters for initial tasks
  const searchParams = getUrlParams();
  const hasInitialTasksParam =
    searchParams.get('new') === 'true' || searchParams.get('new') === '1';
  const getInitialTasks = hasInitialTasksParam && !userInfo?.loggedIn;

  // Prepare full query parameters (with all filters)
  const fullQueryParams: GetTasksParams = {
    current_page_url: pageFilter === 'current' ? currentPageUrl : undefined,
    notified_users: notifiedUsers,
    order_by: orderBy,
    task_status: taskStatusFilter,
    task_types: taskTypesFilter,
    text_search: textSearch,
    workspace_id: workspaceId,
    get_initial_tasks: getInitialTasks,
    limit: totalTasks,
    siteId: siteId ?? '',
    offset,
  };

  // Determine if query should be skipped
  const shouldSkip = !siteId || (!currentPageUrl && pageFilter === 'current');

  // Execute the query
  const query = useGetTasksQuery(fullQueryParams, {
    skip: shouldSkip,
  });

  useEffect(() => {
    if (query.currentData) {
      const updatedTasks = getCurrentTasks();
      // Only keep true PENDING tasks (drafts not yet submitted)
      // Everything else should be replaced by fresh server data
      const updatedTasksFiltered = updatedTasks.filter(
        (task) =>
          task.task_id.includes('PENDING') &&
          !task.task_id.includes('OPTIMISTIC')
      );

      // Combine tasks and deduplicate by task_comment_id to prevent duplicate keys
      const combinedTasks = [
        ...updatedTasksFiltered,
        ...query.currentData.result.tasks,
      ];

      // Deduplicate by task_comment_id - prefer real tasks over optimistic ones
      const taskMap = new Map();
      combinedTasks.forEach((task) => {
        const key = task.task_comment_id;
        if (!taskMap.has(key)) {
          taskMap.set(key, task);
        } else {
          // If we already have a task with this comment_id, prefer the real task
          const existing = taskMap.get(key);
          if (
            existing.task_id.includes('OPTIMISTIC') &&
            !task.task_id.includes('OPTIMISTIC')
          ) {
            taskMap.set(key, task);
          }
        }
      });

      const newTasks = Array.from(taskMap.values());
      dispatch(setTasks(newTasks as CollaborateTaskData[]));
    }
  }, [query.currentData, dispatch, getCurrentTasks]);

  // Check if a task is on the current page
  const isTaskOnCurrentPage = useCallback(
    (task: CollaborateTaskData) => {
      if (getInitialTasks) return true;
      try {
        const pageResult = getHostAndPath(window.location.href);
        if (!pageResult) return false;
        const taskResult = getHostAndPath(task.page_url);
        if (!taskResult) return false;
        if (isInIframe()) {
          // check just the paths as the proxy will mess up the host
          return pageResult.path === taskResult.path;
        }
        return (
          pageResult.host === taskResult.host &&
          pageResult.path === taskResult.path
        );
      } catch (_e) {
        return false;
      }
    },
    [getInitialTasks]
  );

  return {
    // Raw query results
    query,
    rawData: query.data,
    currentData: query.currentData,

    // Processed task data
    tasks: query.data?.result.tasks || [],
    hasNoTasks:
      !query.data?.result.tasks || query.data.result.tasks.length === 0,

    // Totals
    totalRecords: query.data?.total_records,
    totalRecordsAssignedToMe: query.data?.total_records_assigned_to_me,

    // Statuses
    isLoading: query.isLoading,
    isSuccess: query.isSuccess,
    isError: query.isError,
    error: query.error,

    // Functions
    refetch: query.refetch,
    isTaskOnCurrentPage,

    // Context
    siteId,
    currentPageUrl,
    workspaceId,
    initialTasks: getInitialTasks,

    // Filters (including processed values)
    filters: {
      pageFilter,
      notifiedUsers,
      orderBy,
      taskStatus: taskStatusFilter,
      taskTypes: taskTypesFilter,
      textSearch,
      getInitialTasks,
    },
  };
};
